import { Axios, AxiosResponse } from 'axios'

import { Organisation, Partner } from '../admin/admin.types'

import { PBPGetResponse, PBPListResponse } from '@percent/admin-dashboard/services/pbpResponses'
import {
  GetDonationListProps,
  GetTotalDonationsWithAmountProps,
  Donation,
  DonationEnhanced,
  PostDonationsBatchCsvProps,
  PostDonationsBatchProps,
  PostDonationsBatchResponse,
  TotalDonationsCountWithTotal,
  PostReassignAllDonationsProps,
  PostReassignAllDonationsResponse,
  AddDonationToReassignmentQueueProps,
  RemoveDonationFromReassignmentQueueProps,
  ReassignMarkedDonationProps,
  ReassignMarkedDonationsProps,
  GetDonationReassignmentListProps,
  AddAllDonationsToReassignmentQueueProps,
  ReassignmentQueueBulkStatistics,
  ReassignMarkedDonationsResponse,
  PostCancelAllReassignmentsProps,
  PostCancelAllReassignmentsResponse,
  PostDonationReassignmentsBatchProps,
  PostDonationReassignmentsBatchResponse,
  GetPayrollDeductionReportProps,
  PostPayrollDeductionReportsImportProps,
  PostPayrollDeductionReportsImportResponse,
  EditDonationProps
} from './donations.types'

const maxRelatedRecordPageSize = 100

const relatedRecordPageSize = (ids: string[]) => Math.min(ids.length, maxRelatedRecordPageSize)

export const donationsService = ({ percentClient }: { percentClient: Axios }) => ({
  getDonations: async (params: GetDonationListProps): Promise<AxiosResponse<PBPListResponse<DonationEnhanced>>> => {
    const donations = await percentClient.get<PBPListResponse<Donation>>('v1/admin/donations', {
      params
    })

    if (donations.data.data.length === 0 || donations.status !== 200) {
      return donations as AxiosResponse<PBPListResponse<DonationEnhanced>>
    }
    const partnerIds = Array.from(new Set(donations.data.data.map(({ partnerId }) => partnerId)))
    const organisationIds = Array.from(new Set(donations.data.data.map(({ organisationId }) => organisationId)))
    const [partners, organisations] = await Promise.all([
      percentClient.get<PBPListResponse<Partner>>('v1/admin/partners', {
        params: { partnerIds, pageSize: relatedRecordPageSize(partnerIds) }
      }),
      percentClient.get<PBPListResponse<Organisation>>('v1/admin/organisations', {
        params: { organisationIds, pageSize: relatedRecordPageSize(organisationIds) }
      })
    ])

    if (partners.status !== 200) {
      return partners as unknown as AxiosResponse<PBPListResponse<DonationEnhanced>>
    }

    if (organisations.status !== 200) {
      return organisations as unknown as AxiosResponse<PBPListResponse<DonationEnhanced>>
    }
    const partnerMap = new Map(partners.data.data.map(props => [props.id, props]))
    const organisationsMap = new Map(organisations.data.data.map(props => [props.id, props]))

    return {
      ...donations,
      data: {
        ...donations.data,
        data: donations.data.data.map(props => ({
          ...props,
          partner: partnerMap.get(props.partnerId),
          organisation: organisationsMap.get(props.organisationId)
        }))
      }
    }
  },
  reassignAllDonations: async ({ organisationId, payload }: PostReassignAllDonationsProps) =>
    percentClient.post<PBPGetResponse<PostReassignAllDonationsResponse>>(
      `v1/admin/donations/organisations/${organisationId}/reassign`,
      payload
    ),
  uploadDonationsBatchCsv: ({ donationsBatchCSV }: PostDonationsBatchCsvProps) =>
    percentClient.post<any>('v1/admin/donations-batch', donationsBatchCSV, {
      headers: {
        'Content-Type': 'text/csv'
      }
    }),
  uploadDonationsBatch: ({ payload }: PostDonationsBatchProps) =>
    percentClient.post<PostDonationsBatchResponse>('v1/admin/donations-batch', payload),
  uploadDonationReassignmentsBatch: ({ payload }: PostDonationReassignmentsBatchProps) =>
    percentClient.post<PostDonationReassignmentsBatchResponse>('v1/admin/donation-reassignments-batch', payload, {
      headers: {
        'Content-Type': 'text/csv'
      }
    }),
  downloadReassignmentsCsv: async ({
    reassignmentKinds,
    reassignmentStatuses
  }: GetDonationReassignmentListProps): Promise<string> => {
    const { data } = await percentClient.get<string>('v1/admin/donations/reassignments', {
      responseType: 'blob',
      headers: { 'Content-Type': 'text/csv' },
      params: { reassignmentKind: reassignmentKinds, reassignmentStatus: reassignmentStatuses }
    })

    return data
  },

  getTotalDonationsWithAmount: async ({
    organisationId,
    currencyCode
  }: GetTotalDonationsWithAmountProps): Promise<AxiosResponse<PBPGetResponse<TotalDonationsCountWithTotal>>> =>
    percentClient.get<PBPGetResponse<TotalDonationsCountWithTotal>>(
      `v1/admin/donations/organisations/${organisationId}/total`,
      {
        params: {
          currency: currencyCode
        }
      }
    ),
  addDonationToReassignmentQueue: async ({ donationId, payload }: AddDonationToReassignmentQueueProps) =>
    percentClient.post<PBPGetResponse<Donation>>(`v1/admin/donations/${donationId}/mark-for-reassignment`, payload),
  removeDonationFromReassignmentQueue: async ({ donationId }: RemoveDonationFromReassignmentQueueProps) =>
    percentClient.post<PBPGetResponse<Donation>>(`v1/admin/donations/${donationId}/cancel-reassignment`),
  reassignMarkedDonation: async ({ donationId, payload }: ReassignMarkedDonationProps) =>
    percentClient.post<PBPGetResponse<Donation>>(`v1/admin/donations/${donationId}/reassign-marked`, payload),
  reassignMarkedDonations: async ({ organisationId, payload }: ReassignMarkedDonationsProps) =>
    percentClient.post<PBPGetResponse<ReassignMarkedDonationsResponse>>(
      `v1/admin/donations/organisations/${organisationId}/reassign-marked`,
      payload
    ),
  addAllDonationsToReassignmentQueue: async ({ organisationId, payload }: AddAllDonationsToReassignmentQueueProps) =>
    percentClient.post<PBPGetResponse<ReassignmentQueueBulkStatistics>>(
      `v1/admin/donations/organisations/${organisationId}/mark-for-reassignment`,
      payload
    ),
  cancelAllReassignments: async ({ organisationId }: PostCancelAllReassignmentsProps) =>
    percentClient.post<PBPGetResponse<PostCancelAllReassignmentsResponse>>(
      `v1/admin/donations/organisations/${organisationId}/cancel-all-reassignment`,
      { organisationId }
    ),
  downloadPayrollDeductionReport: async ({
    partnerId,
    startDate,
    endDate
  }: GetPayrollDeductionReportProps): Promise<string> => {
    const { data } = await percentClient.get<string>('v1/admin/payroll-deduction-reports', {
      responseType: 'blob',
      headers: { 'Content-Type': 'text/csv' },
      params: { partnerId, startDate, endDate }
    })

    return data
  },
  uploadPayrollDeductionReportsImport: async ({ payload, partnerId }: PostPayrollDeductionReportsImportProps) =>
    percentClient.post<PostPayrollDeductionReportsImportResponse>(
      `v1/admin/payroll-deduction-reports/import?partnerId=${partnerId}`,
      payload,
      {
        headers: {
          'Content-Type': 'text/csv'
        }
      }
    ),
  editDonation: async ({ donationId, payload }: EditDonationProps) =>
    percentClient.patch<PBPGetResponse<Donation>>(`v1/admin/donations/${donationId}`, payload)
})
